import React, { useState } from 'react';
import { usePortfolios } from '../../../hooks/use-portfolios';
import {
  ErrorLabel,
  FieldHint,
  InputContainer,
  InteractiveFieldAction,
  Label,
  StyledBuildingInput,
  StyledBuildingSelect,
} from './dashboard-buildings.styled';

// The "which portfolio does this building live in" field, shared by the add and
// the edit property forms so the two can never drift apart.
//
// Renders NOTHING when the signed-in user lacks portfolio:read — usePortfolios
// reports that through `allowed` and fires no request at all. A concierge holds
// building:read and reaches these forms without ever being a portfolio
// audience, and an empty picker would imply the org has no portfolios rather
// than that this user is not shown them.
//
// Only the create form can mint a portfolio inline: the add-property route
// accepts new_portfolio_name alongside portfolio_id, whereas the edit route is
// allowlisted against mass-assignment and moves a building through its own
// endpoint. Edit therefore offers existing portfolios only.

interface BuildingPortfolioFieldProps {
  mode: 'create' | 'edit';
  portfolioId: string;
  newPortfolioName: string;
  onChange: (next: { portfolioId: string; newPortfolioName: string }) => void;
  disabled?: boolean;
}

export const BuildingPortfolioField: React.FC<BuildingPortfolioFieldProps> = ({
  mode,
  portfolioId,
  newPortfolioName,
  onChange,
  disabled,
}) => {
  const { portfolios, isLoading, error, allowed } = usePortfolios();
  const [isNamingNewPortfolio, setIsNamingNewPortfolio] =
    useState<boolean>(false);

  if (!allowed) {
    return null;
  }

  // Reading the parent's value too, so the input survives a remount with a name
  // already typed instead of silently dropping back to the select.
  const showNewPortfolioInput =
    mode === 'create' && (isNamingNewPortfolio || newPortfolioName.length > 0);

  const fieldId =
    mode === 'create' ? 'new-building-portfolio' : 'edit-building-portfolio';

  // The building's current portfolio is set but absent from the (active-only)
  // list — i.e. it was archived. Only reachable in edit mode, where the value is
  // seeded from the building row.
  const isOrphanedSelection =
    portfolioId !== '' &&
    !isLoading &&
    !portfolios.some(portfolio => String(portfolio.id) === portfolioId);

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ portfolioId: event.target.value, newPortfolioName: '' });
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ portfolioId: '', newPortfolioName: event.target.value });
  };

  // Both toggles clear both halves: the server reads portfolio_id and
  // new_portfolio_name from the same form, and leaving a stale value behind the
  // hidden control would file the building somewhere the user cannot see.
  const startNamingNewPortfolio = () => {
    setIsNamingNewPortfolio(true);
    onChange({ portfolioId: '', newPortfolioName: '' });
  };

  const useExistingPortfolio = () => {
    setIsNamingNewPortfolio(false);
    onChange({ portfolioId: '', newPortfolioName: '' });
  };

  if (showNewPortfolioInput) {
    return (
      <>
        <InputContainer>
          <Label htmlFor={`${fieldId}-new-name`}>New portfolio</Label>
          <StyledBuildingInput
            id={`${fieldId}-new-name`}
            type="text"
            name="new_portfolio_name"
            maxLength={200}
            value={newPortfolioName}
            placeholder={'Enter portfolio name'}
            onChange={handleNameChange}
            disabled={disabled}
          />
        </InputContainer>
        {/* Carried into this branch too. A failed portfolio load is exactly why
            the select looked empty, and clicking "create a new portfolio" in
            response must not silently drop the only notice of that failure. */}
        {error && <ErrorLabel>{error}</ErrorLabel>}
        <FieldHint>
          The portfolio is created with the property and this building becomes
          its first.
        </FieldHint>
        <InteractiveFieldAction type="button" onClick={useExistingPortfolio}>
          x Use an existing portfolio
        </InteractiveFieldAction>
      </>
    );
  }

  return (
    <>
      <InputContainer>
        <Label htmlFor={fieldId}>Portfolio</Label>
        <StyledBuildingSelect
          id={fieldId}
          name="portfolio_id"
          value={portfolioId}
          onChange={handleSelectChange}
          disabled={disabled || isLoading}
        >
          <option value="">
            {mode === 'create' ? 'Organisation default' : 'Keep where it is'}
          </option>
          {/* A building can sit in an ARCHIVED portfolio, and this list is
              active-only. Without this the select would hold a real id while
              painting "Keep where it is" — telling the owner the building is
              somewhere it is not, on the very screen they came to move it from.
              Disabled because an archived portfolio is not a legal
              destination; picking any other option moves it out. */}
          {isOrphanedSelection && (
            <option value={portfolioId} disabled>
              Current portfolio (archived)
            </option>
          )}
          {portfolios.map(portfolio => (
            <option key={portfolio.id} value={String(portfolio.id)}>
              {portfolio.is_default
                ? `${portfolio.name} (default)`
                : portfolio.name}
            </option>
          ))}
        </StyledBuildingSelect>
      </InputContainer>
      {error && <ErrorLabel>{error}</ErrorLabel>}
      <FieldHint>
        {mode === 'create'
          ? 'Leave this empty and the building is filed under your organisation’s default portfolio.'
          : 'Leave this empty to keep the building in the portfolio it is in today.'}
      </FieldHint>
      {mode === 'create' && (
        <InteractiveFieldAction type="button" onClick={startNamingNewPortfolio}>
          + Create a new portfolio
        </InteractiveFieldAction>
      )}
    </>
  );
};
